import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Upload, X, CheckCircle2, Phone, MessageCircle, Instagram, FileVideo, Image as ImageIcon, Loader2, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, Section, PrimaryButton, SecondaryButton } from "@/components/ui-helpers";
import { Progress } from "@/components/ui/progress";
import { api, uploadFile, mediaUrl } from "@/lib/api";
import { useSite, whatsappLink } from "@/context/SiteContext";
import { AvailabilityPicker } from "@/components/AvailabilityPicker";
import { Events } from "@/lib/analytics";
import { toast } from "sonner";

const SERVICES = ["Post-Construction Cleaning", "Move-In / Move-Out Cleaning", "General Cleaning", "General Deep Cleaning", "Property Detailing", "Bathroom Detail", "Kitchen Detail", "Window Detail", "Appliance Detail", "Balcony Detail", "Not sure yet"];
const PROPERTY_TYPES = ["Apartment", "House", "Penthouse", "Office", "Commercial", "Other"];
const TOTAL_STEPS = 4;
const STEP_LABELS = ["Your details", "Service & property", "Photos & timing", "Review & submit"];

const Field = ({ label, children }) => (
  <label className="block"><span className="block text-sm text-secondary mb-1.5">{label}</span>{children}</label>
);
const inputCls = "w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]";

export default function Contact() {
  const site = useSite();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [media, setMedia] = useState([]);
  const [f, setF] = useState({
    full_name: "", phone: "", email: "", preferred_language: "English", address: "", city: "",
    property_type: "", service_required: "", size_sqm: "", rooms: "", bathrooms: "", furnishing: "",
    property_state: "", balcony_size: "", desired_date: "", desired_slot: "", deadline: "", priority_areas: "",
    notes: "", budget_range: "", preferred_contact: "WhatsApp", consent: false,
  });
  const aiMeta = React.useRef({ ai_summary: "", ai_collected: null });
  const [fromAsk, setFromAsk] = useState(false);
  const [prefillSource, setPrefillSource] = useState(null);
  const [rescue, setRescue] = useState(false);
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    // Detect if the visitor already has a client session, to tailor the post-submit nudge.
    api.get("/client/me").then(() => setIsClient(true)).catch(() => setIsClient(false));
  }, []);
  useEffect(() => {
    // Prefill from Ask Ascendia or Property Intelligence (sessionStorage) so nothing is re-entered.
    const pf = params.get("prefill");
    Events.quoteStarted({ entry: pf || params.get("intent") || "direct" });
    if (pf === "ask" || pf === "intelligence" || pf === "estimate") {
      try {
        const raw = sessionStorage.getItem("ascendia_prefill");
        if (raw) {
          const p = JSON.parse(raw);
          setF((prev) => ({
            ...prev,
            ...(p.service_required ? { service_required: p.service_required } : {}),
            ...(p.property_type ? { property_type: p.property_type } : {}),
            ...(p.city ? { city: p.city } : {}),
            ...(p.size_sqm ? { size_sqm: String(p.size_sqm) } : {}),
            ...(p.bathrooms ? { bathrooms: String(p.bathrooms) } : {}),
            ...(p.rooms ? { rooms: String(p.rooms) } : {}),
            ...(p.furnishing ? { furnishing: p.furnishing } : {}),
            ...(p.property_state ? { property_state: p.property_state } : {}),
            ...(p.deadline ? { deadline: p.deadline } : {}),
            ...(p.priority_areas ? { priority_areas: p.priority_areas } : {}),
            ...(p.notes ? { notes: p.notes } : {}),
          }));
          if (Array.isArray(p.media) && p.media.length) {
            setMedia(p.media.map((m) => ({ ...m, preview: mediaUrl(m.url) })));
          }
          aiMeta.current = { ai_summary: p.ai_summary || "", ai_collected: p.ai_collected || null };
          setFromAsk(true);
          setPrefillSource(pf);
        }
        // NOTE: do NOT clear sessionStorage here — the shared property session must persist
        // so the user can move back and forth across tools (Estimate/Property Intelligence/Ask)
        // without re-entering data. It is cleared only after a successful submission.
      } catch (e) { console.error("Ask Ascendia prefill parse failed:", e); }
      return;
    }
    const s = params.get("service");
    const extras = params.get("extras");
    const intent = params.get("intent");
    if (intent === "rescue") {
      setRescue(true);
      const URGENCY_LABELS = { "today": "Today", "24h": "Within 24 hours", "this-week": "This week", "flexible": "Flexible timing" };
      const SCENARIO_LABELS = {
        "cleaners-fell-short": "Previous cleaners fell short",
        "move-in-deadline": "Move-in deadline is imminent",
        "contractor-mess": "Contractor left it dirty",
        "last-minute-handover": "Last-minute property handover",
      };
      const urgencyLabel = URGENCY_LABELS[params.get("urgency")] || null;
      const scenarioLabel = SCENARIO_LABELS[params.get("scenario")] || null;
      const rescueNotes = [
        "ASCENDIA RESCUE — urgent request.",
        urgencyLabel ? `How soon: ${urgencyLabel}` : null,
        scenarioLabel ? `Situation: ${scenarioLabel}` : "Situation (please describe): previous cleaners fell short / imminent move-in deadline / contractor left it dirty / last-minute handover.",
        "Please advise your earliest availability. (Availability and pricing to be confirmed.)",
      ].filter(Boolean).join("\n");
      setF((p) => ({
        ...p,
        priority_areas: p.priority_areas || (scenarioLabel ? `Urgent / rescue clean — ${scenarioLabel}` : "Urgent / rescue clean"),
        notes: p.notes || rescueNotes,
      }));
    }
    setF((p) => ({ ...p, ...(s ? { service_required: s } : {}), ...(extras ? { priority_areas: extras } : {}) }));
  }, [params]);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const set = (k, v) => setF({ ...f, [k]: v });

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try { const res = await uploadFile(file); setMedia((prev) => [...prev, { ...res, preview: mediaUrl(res.url) }]); }
      catch { toast.error(`Couldn't upload ${file.name}`); }
    }
    setUploading(false); e.target.value = "";
  };
  const removeMedia = (path) => setMedia((prev) => prev.filter((m) => m.path !== path));

  const next = () => {
    if (step === 1 && (!f.full_name || !f.phone)) { toast.error("Please enter your name and phone."); return; }
    if (step === 2 && !f.service_required) { toast.error("Please choose a service."); return; }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!f.consent) { toast.error("Please accept the consent checkbox to continue."); return; }
    setBusy(true);
    try {
      const payload = { ...f, source: rescue ? "rescue" : prefillSource === "intelligence" ? "property-intelligence" : prefillSource === "estimate" ? "estimate" : (fromAsk ? "ask-ascendia" : "website"), ...aiMeta.current, media: media.map(({ path, url, original_filename, content_type }) => ({ path, url, original_filename, content_type })) };
      const { data } = await api.post("/quote-requests", payload);
      Events.quoteSubmitted({ source: payload.source, service: f.service_required || undefined, has_media: media.length > 0 });
      // If the client picked a preferred window, create a linked booking request (best-effort)
      if (f.desired_date && f.desired_slot) {
        try {
          await api.post("/booking-requests", {
            name: f.full_name, phone: f.phone, email: f.email,
            service: f.service_required, date: f.desired_date, slot: f.desired_slot,
            notes: f.notes, lead_id: data.id, lead_ref: data.ref, source: "quote",
          });
          Events.bookingRequested({ source: "quote", service: f.service_required || undefined });
        } catch (bookErr) { console.warn("Optional booking request failed (quote was still submitted):", bookErr); }
      }
      setResult(data);
      // Journey complete — clear the shared property session now that the lead is submitted.
      try { sessionStorage.removeItem("ascendia_prefill"); } catch (storageErr) { console.debug("Could not clear saved session:", storageErr); }
    } catch (e) { console.error("Quote submission failed:", e); toast.error("Something went wrong. Please try again or WhatsApp us."); } finally { setBusy(false); }
  };

  if (result) {
    return (
      <>
        <SEO title="Quote Requested" />
        <Section className="pt-28">
          <div data-testid="quote-confirmation-panel" className="ascendia-card card-accent-top max-w-xl mx-auto p-8 text-center">
            <CheckCircle2 className="h-14 w-14 text-gold mx-auto" />
            <h1 className="mt-5 font-display text-2xl font-semibold text-white">Thank you, {f.full_name.split(" ")[0]}!</h1>
            <p className="mt-3 text-secondary">Your request has been received. Reference <span className="font-mono text-gold-soft">{result.ref}</span>. Our team will review your details{media.length ? " and media" : ""} and prepare a tailored quote.</p>
            <div className="mt-6 rounded-xl border border-[color:var(--border-subtle)] bg-black/30 p-4 text-left">
              <p className="text-xs uppercase tracking-[0.14em] text-muted-c mb-2">What happens next</p>
              <ol className="space-y-1 text-sm text-secondary list-decimal list-inside">
                <li>We review your property details and photos/video.</li>
                <li>We prepare and send a tailored quote.</li>
                <li>You confirm a date and we get to work.</li>
              </ol>
            </div>
            <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
              <PrimaryButton href={whatsappLink(site)} target="_blank" rel="noopener noreferrer"><MessageCircle className="h-4 w-4" /> Continue on WhatsApp</PrimaryButton>
              <SecondaryButton href={`tel:${site.phone}`}><Phone className="h-4 w-4" /> Call {site.phone}</SecondaryButton>
            </div>

            {!isClient && (
              <div className="mt-6 rounded-xl border border-[color:var(--border-strong)] bg-gold/[0.06] p-5 text-left" data-testid="guest-account-nudge">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-gold-soft flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display font-semibold text-white">Create your Ascendia account</p>
                    <p className="mt-1 text-sm text-secondary">Save this property and track your quote, bookings and completion reports in one private area.</p>
                    <a href={`/account/signup?email=${encodeURIComponent(f.email || "")}&phone=${encodeURIComponent(f.phone || "")}&name=${encodeURIComponent(f.full_name || "")}`} data-testid="guest-nudge-create-account" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft">Create my account <ArrowRight className="h-4 w-4" /></a>
                    <a href="/account/login" className="ml-3 text-sm text-gold-soft hover:text-gold">I already have one</a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Section>
      </>
    );
  }

  return (
    <>
      <SEO title="Request a Quote" description="Request a tailored quote from Ascendia Detail. Upload photos or a walkthrough video for accurate pricing." />
      <PageHero kicker="Request a Quote" title="Tell us about your property" subtitle="Share a few details and upload photos or a walkthrough video for the most accurate, tailored quote." />
      <Section>
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="mb-6">
              {rescue && (
                <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/[0.08] px-4 py-3" data-testid="rescue-banner">
                  <Sparkles className="h-4 w-4 text-amber-300 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-secondary"><span className="font-semibold text-amber-200">Ascendia Rescue</span> — we'll prioritise your urgent assessment. Tell us the situation and your deadline below. Availability and pricing to be confirmed.</p>
                </div>
              )}
              {fromAsk && (
                <div className="mb-4 flex items-start gap-2 rounded-xl border border-[color:var(--border-strong)] bg-gold/[0.06] px-4 py-3" data-testid="prefill-banner">
                  <Sparkles className="h-4 w-4 text-gold-soft flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-secondary">{prefillSource === "intelligence" ? "We've pre-filled your details from your Ascendia Property Plan. Please review and add anything missing." : prefillSource === "estimate" ? "We've carried over the details from your estimate. Please review and add anything missing." : "We've pre-filled your details from your chat with Ask Ascendia. Please review and add anything missing."}</p>
                </div>
              )}
              <div className="flex justify-between text-xs text-muted-c mb-2"><span>Step {step} of {TOTAL_STEPS}</span><span>{STEP_LABELS[step - 1]}</span></div>
              <Progress value={(step / TOTAL_STEPS) * 100} className="h-1.5 bg-white/10" />
            </div>

            <div className="ascendia-card card-accent-top p-6 sm:p-8">
              {step === 1 && (
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name*"><input className={inputCls} value={f.full_name} onChange={upd("full_name")} data-testid="quote-full-name-input" placeholder="Your name" /></Field>
                  <Field label="Phone*"><input className={inputCls} value={f.phone} onChange={upd("phone")} data-testid="quote-phone-input" placeholder="05x-xxx-xxxx" /></Field>
                  <Field label="Email"><input className={inputCls} value={f.email} onChange={upd("email")} placeholder="you@example.com" /></Field>
                  <Field label="Preferred language"><select className={inputCls} value={f.preferred_language} onChange={upd("preferred_language")}><option>English</option><option>Hebrew</option></select></Field>
                  <Field label="Property address"><input className={inputCls} value={f.address} onChange={upd("address")} placeholder="Street & number" /></Field>
                  <Field label="City"><input className={inputCls} value={f.city} onChange={upd("city")} placeholder="e.g. Tel Aviv" /></Field>
                </div>
              )}
              {step === 2 && (
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Service required*"><select className={inputCls} value={f.service_required} onChange={upd("service_required")} data-testid="quote-service-select"><option value="">Select a service</option>{SERVICES.map((s) => <option key={s}>{s}</option>)}</select></Field>
                  <Field label="Property type"><select className={inputCls} value={f.property_type} onChange={upd("property_type")}><option value="">Select</option>{PROPERTY_TYPES.map((s) => <option key={s}>{s}</option>)}</select></Field>
                  <Field label="Size (sqm)"><input className={inputCls} value={f.size_sqm} onChange={upd("size_sqm")} placeholder="e.g. 90" /></Field>
                  <Field label="Rooms"><input className={inputCls} value={f.rooms} onChange={upd("rooms")} placeholder="e.g. 4" /></Field>
                  <Field label="Bathrooms"><input className={inputCls} value={f.bathrooms} onChange={upd("bathrooms")} placeholder="e.g. 2" /></Field>
                  <Field label="Balcony size"><input className={inputCls} value={f.balcony_size} onChange={upd("balcony_size")} placeholder="e.g. small / none" /></Field>
                  <Field label="Furnishing"><select className={inputCls} value={f.furnishing} onChange={upd("furnishing")}><option value="">Select</option><option>Empty</option><option>Furnished</option></select></Field>
                  <Field label="Property state"><select className={inputCls} value={f.property_state} onChange={upd("property_state")}><option value="">Select</option><option>Post-construction</option><option>Previously lived in</option></select></Field>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-5">
                  <div>
                    <span className="block text-sm text-secondary mb-1.5">Upload photos or a walkthrough video</span>
                    <label className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[color:var(--border-strong)] bg-black/30 py-8 cursor-pointer hover:bg-white/[0.03] transition-colors">
                      <Upload className="h-6 w-6 text-gold-soft" />
                      <span className="text-sm text-secondary">Click to upload (images & video, up to 60MB each)</span>
                      <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={onUpload} data-testid="quote-media-upload-input" />
                    </label>
                    {uploading && <p className="mt-2 text-sm text-muted-c flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</p>}
                    {media.length > 0 && (
                      <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {media.map((m) => (
                          <div key={m.path} className="relative rounded-lg overflow-hidden border border-[color:var(--border-subtle)] aspect-square bg-black/40">
                            {m.content_type?.startsWith("image") ? <img src={m.preview} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center"><FileVideo className="h-7 w-7 text-gold-soft" /></div>}
                            <button onClick={() => removeMedia(m.path)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="rounded-xl border border-[color:var(--border-subtle)] bg-black/20 p-4">
                    <span className="block text-sm text-white font-medium mb-1">Request a preferred window <span className="text-muted-c font-normal">(optional)</span></span>
                    <p className="text-xs text-muted-c mb-4">Pick an open day and time window. This is a request only — we'll confirm the final time with you.</p>
                    <AvailabilityPicker
                      compact
                      value={{ date: f.desired_date, slot: f.desired_slot }}
                      onChange={({ date, slot }) => setF((p) => ({ ...p, desired_date: date, desired_slot: slot }))}
                    />
                  </div>
                  <Field label="Deadline / move-in date"><input type="date" className={inputCls} value={f.deadline} onChange={upd("deadline")} /></Field>
                  <Field label="Priority areas"><input className={inputCls} value={f.priority_areas} onChange={upd("priority_areas")} placeholder="e.g. kitchen, bathrooms, windows" /></Field>
                  <Field label="Additional notes"><textarea rows={3} className={inputCls} value={f.notes} onChange={upd("notes")} placeholder="Anything else we should know?" /></Field>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Budget range (optional)"><input className={inputCls} value={f.budget_range} onChange={upd("budget_range")} placeholder="optional" /></Field>
                    <Field label="Preferred contact method"><select className={inputCls} value={f.preferred_contact} onChange={upd("preferred_contact")}><option>WhatsApp</option><option>Phone call</option><option>Email</option></select></Field>
                  </div>
                </div>
              )}
              {step === TOTAL_STEPS && (
                <div className="space-y-5">
                  <h3 className="font-display text-lg font-semibold text-white">Review your request</h3>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {[["Name", f.full_name], ["Phone", f.phone], ["Email", f.email], ["Service", f.service_required], ["City", f.city], ["Size", f.size_sqm && `${f.size_sqm} sqm`], ["Rooms", f.rooms], ["Bathrooms", f.bathrooms], ["State", f.property_state], ["Requested window", f.desired_date && f.desired_slot ? `${new Date(f.desired_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${f.desired_slot}` : ""], ["Contact via", f.preferred_contact], ["Media", `${media.length} file(s)`]].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-[color:var(--border-subtle)]/50 py-1.5"><span className="text-muted-c">{k}</span><span className="text-white text-right">{v}</span></div>
                    ))}
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={f.consent} onChange={upd("consent")} data-testid="quote-consent-checkbox" className="mt-1 h-4 w-4 accent-[#C8A14A]" />
                    <span className="text-sm text-secondary">I consent to Ascendia Detail contacting me about this request and storing my details to prepare a quote.</span>
                  </label>
                </div>
              )}

              <div className="mt-8 flex justify-between">
                {step > 1 ? <button onClick={back} data-testid="quote-prev-step-button" className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button> : <span />}
                {step < TOTAL_STEPS
                  ? <button onClick={next} data-testid="quote-next-step-button" className="inline-flex items-center gap-2 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft transition-colors">Continue <ArrowRight className="h-4 w-4" /></button>
                  : <button onClick={submit} disabled={busy} data-testid="quote-submit-button" className="inline-flex items-center gap-2 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft transition-colors disabled:opacity-50">{busy ? "Submitting…" : "Submit Request"} <ArrowRight className="h-4 w-4" /></button>}
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="ascendia-card p-6">
              <h3 className="font-display text-lg font-semibold text-white">Prefer to talk?</h3>
              <div className="mt-4 space-y-3">
                <a href={`tel:${site.phone}`} className="flex items-center gap-3 text-sm text-secondary hover:text-white"><Phone className="h-4 w-4 text-gold-soft" /> {site.phone}</a>
                <a href={whatsappLink(site)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-secondary hover:text-white"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp us</a>
                <a href={site.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-secondary hover:text-white"><Instagram className="h-4 w-4 text-gold-soft" /> {site.instagram_handle}</a>
              </div>
            </div>
            <div className="ascendia-card p-6">
              <div className="flex items-center gap-2 text-gold-soft"><ImageIcon className="h-4 w-4" /><p className="text-sm font-medium">Why upload media?</p></div>
              <p className="mt-2 text-sm text-muted-c">Photos and a short walkthrough video let us price accurately without a site visit — and there are no surprises on the day.</p>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
